import React, { useState, useEffect, useRef } from 'react';

import Grid from '@mui/material/Grid2';
import { 
  Character,
  CharacterResource,
  CharacterSheetInterface,
  TransformInstance,
  SpellcastingFeature,
  CharacterInfusionsOfLevel,
  SpellSlotType,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import CharacterResourceBoxes from "@dndComponents/character/misc/CharacterResourceBoxes";
import ConcentrationInput from "@dndComponents/character/spells_and_abilities/ConcentrationInput";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterSlotsForType from "@dndComponents/character/bar3/CharacterSlotsForType";
import LoadingBox from "@components/input/LoadingBox";
import DataUtilities from '@dndUtilities/data_utilities';

interface Props {
  char: Character; 
  char_interface: CharacterSheetInterface;
  shared: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterBar3({ 
  char,
  char_interface,
  shared,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [display_me, setDisplayMe] = useState<CharacterResource | string | null>(null);
  const [spell_slot_types, setSpellSlotTypes] = useState<SpellSlotType[]>([]);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = () => {
    const slots = char_interface.use_slots.filter(o => o.slot_name === display_me);
    const slot_types = common_util.unique_array(slots.map(o => o.type_id));
    if (slot_types.length > 0) {
      setLoading(true);
      data_util.getSpellSlotTypes().then(res => {
        setSpellSlotTypes(res);
        setLoading(false);
        setReloading(true);
      });
    } else {
      setReloading(true);
    }
  }

  useEffect(() => {
    if (!fetched) {
      char.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
      load();
      setFetched(true);
    }
    // eslint-disable-next-line
  }, [fetched, setFetched]);

  const resourcesChanged = () => {
    load();
  }

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const updateCharacter = (pieces: string[] = ["all"]) => {
    // debugger;
    char.changed();
    // onChange();
    char_util.resourcesChanged(char);
    // setReloading(true);
  }

  const renderSlotsForType = (slot_name: string) => {
    return (
      <CharacterSlotsForType key={slot_name} 
        character={char} 
        char_interface={char_interface} 
        slot_name={slot_name} 
        shared={shared}
      />
    )
  }
  const renderResource = (resource: CharacterResource) => {
    return (
      <span key={`${resource.type_id}_${resource.class_id}`} className="padded" style={{ 
        borderRight: "1px solid",
        minWidth: "140px"
      }}>
        <Grid container spacing={0} direction="row">
          <Grid size={12}
            className={ "header centered clickable" }
            onClick={() => {
              setDisplayMe(resource);
              setOpen(true);
            }}>
            { 
              <>
                { resource.name }&nbsp;
                { resource.size > 1 && ` (d${resource.size})` }&nbsp;
              </>
            }
          </Grid>
          { resource.show_source_name && 
            <Grid size={12} className="header centered">
              ({ resource.source_name })
            </Grid>
          }
          { 
            <Grid size={12}>
              <CharacterResourceBoxes  
                used={resource.used}
                total={resource.total}
                true_total={resource.true_total}
                created={ resource.created }
                character={char}
                shared={shared}
                onChange={(used: number) => {
                  const diff = resource.used - used;
                  resource.used = used;
                  let updates = ["hp"];
                  if (char_interface instanceof TransformInstance && char_interface.transform) {
                    const transform = char_interface.transform;
                    let override = transform.resources[resource.type_id];
                    if (!override) {
                      override = transform.resources["Default"];
                    }
                    if (["Share","Merge"].includes(override.override_mode)) {
                      const char_resource = char.resources.find(o => o.type_id === resource.type_id && o.class_id === resource.class_id);
                      if (char_resource) {
                        if (override.share) {
                          char_resource.used = used;
                        } else {
                          char_resource.used += diff;
                        }
                      }
                      updates.push("features");
                    } else {
                      updates = ["features"];
                    }
                  }
                  updateCharacter(updates);
                  // setReloading(true);
                }}
              />
            </Grid>
          }
        </Grid>
      </span>
    );
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer
        open={open}
        onClose={() => { setOpen(false); }}
      >
        { renderDisplayMe() }
      </ModalOrDrawer>
    );
  }

  const toFractionString = (level: number) => {
    if (level === 1) {
      return "Full";
    } else if (level === (1/2)) {
      return "Half";
    } else if (level === (1/3)) {
      return "Third";
    } else if (level === (1/4)) {
      return "Quarter";
    }
    return "";
  }

  const spellLevelNumString = (num: number) => {
    switch (num) {
      case 1: return "First";
      case 2: return "Second";
      case 3: return "Third";
      case 4: return "Fourth";
      case 5: return "Fifth";
      case 6: return "Sixth";
      case 7: return "Seventh";
      case 8: return "Eighth";
      case 9: return "Ninth";
    }
  }

  const renderDisplayMe = () => {
    if (display_me instanceof CharacterResource && display_me.resource_feature) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            className="header centered"
            size={{
              xs: 12
            }}>
            Resource: {display_me.name}
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements display_string={display_me.resource_feature.description} cfs={char_interface} />
          </Grid>
        </Grid>
      );
    } else if (typeof display_me === "string") {
      const slots = char_interface.use_slots.filter(o => o.slot_name === display_me);
      const slot_types = common_util.unique_array(slots.map(o => o.type_id));
      // console.log(slots);
      // console.log(slot_types);
      return (
        <>
          { slot_types.map(st_id => {
            const slot_type = spell_slot_types.find(o => o._id === st_id);
            if (slot_type) {
              const features_with_type = char.spellcasting_features.filter(o => 
                o.floating_feature instanceof SpellcastingFeature &&
                o.floating_feature.table === slot_type._id
              );
              // console.log(slot_type);
              const feature_details: any[] = [];
              let total_level = 0;
              for (const cf of features_with_type) {
                const sf = cf.floating_feature as SpellcastingFeature;
                const char_class = char.classes.find(o => o.true_id === cf.source_true_id);
                if (char_class) {
                  const caster_level = sf.level * char_class.level;
                  total_level += caster_level;
                  feature_details.push(
                    <Grid
                      key={cf.true_id}
                      size={{
                        xs: 12
                      }}>
                      { char_class.name }: { toFractionString(sf.level) } Caster ({caster_level})
                    </Grid>
                  );
                }
              }
              const entry = slot_type.entries.sort((a,b) => b.level - a.level).find(o => o.level <= total_level);
              // console.log(entry);
              if (entry) {
                let entry_details = "You have:\r\n";
                const levels = Object.keys(entry.slots_per_level).map(o => +o);
                for (let i = 0; i < levels.length; ++i) {
                  const level = levels[i];
                  const slots_for_level = entry.slots_per_level[level];
                  if (entry_details.length > 14) {
                    entry_details += ",\r\n";
                    if (i === levels.length - 1) {
                      entry_details += "and ";
                    }
                  }
                  entry_details += `${slots_for_level} ${ spellLevelNumString(level) } Level Slot${slots_for_level === 1 ? "" : "s"}`;
                }
                entry_details += ".";
                return (
                  <Grid container spacing={0} direction="row" key={slot_type._id}>
                    <Grid
                      className="header centered"
                      size={{
                        xs: 12
                      }}>
                      Slot: {slot_type.name}
                    </Grid>
                    { feature_details }
                    { feature_details.length > 1 && 
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        Total Caster Level: { total_level }
                      </Grid>
                    }
                    <Grid
                      size={{
                        xs: 12
                      }}>&nbsp;</Grid>
                    <Grid
                      size={{
                        xs: 12
                      }}>
                      <DisplayStringWithReplacements display_string={ entry_details } />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12
                      }}>&nbsp;</Grid>
                    <Grid
                      size={{
                        xs: 12
                      }}>
                      These refresh when you take a { slot_type.refresh_rule }.
                    </Grid>
                  </Grid>
                );
              }
            } 
            return null;
          })}
        </>
      );
    }
    return <></>;
  }

  const renderInfusionsBySource = () => {
    const return_us: any[] = [];
    const infusion_sources = common_util.unique_array(char_interface.infusions_by_level.map(o => o.source_true_id));
    if (infusion_sources.length > 1) {
      for (const infusion_source_true_id of infusion_sources) {
        const cfs = char.all_char_feature_sources.find(o => o.true_id === infusion_source_true_id);
        if (cfs) {
          const infusions_by_level = char_interface.infusions_by_level.filter(o => o.source_true_id === cfs.true_id);
          const width = infusions_by_level.length < 4 ? 180 : (infusions_by_level.length < 9 ? 360 : 540);
          return_us.push(
            <span key={`infusions_for_${cfs.true_id}`} style={{ 
              borderRight: "1px solid",
              width
            }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  className="header centered clickable"
                  onClick={() => {
                    // setDisplayMe(slot_name);
                    // setOpen(true);
                  }}
                  size={{
                    xs: 12
                  }}>
                  { char_util.getUseName(cfs) } Infusions
                </Grid>
                { renderInfusionsOfSource(cfs.true_id) }
              </Grid>
            </span>
          );
        }
      }
    } else {
      const infusions_by_level = char_interface.infusions_by_level.filter(o => o.source_true_id === infusion_sources[0]);
      const width = infusions_by_level.length < 4 ? 180 : (infusions_by_level.length < 9 ? 360 : 540);
      return_us.push(
        <span key={`infusions`} style={{ 
          borderRight: "1px solid",
          width
        }}>
          <Grid container spacing={0} direction="row">
            <Grid
              className="header centered clickable"
              onClick={() => {
                // setDisplayMe(slot_name);
                // setOpen(true);
              }}
              size={{
                xs: 12
              }}>
              Infusions
            </Grid>
            { renderInfusionsOfSource(infusion_sources[0]) }
          </Grid>
        </span>
      );
    }
    return return_us;
  }

  const renderInfusionsOfSource = (source_true_id: string) => {
    const return_us: any[] = [];
    const infusions_by_level = char_interface.infusions_by_level.filter(o => o.source_true_id === source_true_id);
    let current_group: any[] = [];
    if (infusions_by_level.length < 4) {
      for (let i = 0; i < infusions_by_level.length; ++i) {
        const infusions_of_level = infusions_by_level[i];
        current_group.push(renderInfusionsOfLevel(infusions_of_level));
      }
      return_us.push(
        <Grid
          key="last"
          style={{ width: "180px" }}
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row" style={{ width: "180px" }}>
            { current_group }
          </Grid>
        </Grid>
      );
    } else if (infusions_by_level.length < 7) {
      for (let i = 0; i < infusions_by_level.length; ++i) {
        if (i === 4) {
          return_us.push(
            <Grid
              key={i}
              style={{ width: "180px" }}
              size={{
                xs: 6
              }}>
              <Grid container spacing={0} direction="row" style={{ width: "180px" }}>
                { current_group }
              </Grid>
            </Grid>
          );
          current_group = [];
        }
        const infusions_of_level = infusions_by_level[i];
        current_group.push(renderInfusionsOfLevel(infusions_of_level));
      }
      return_us.push(
        <Grid
          key="last"
          style={{ width: "180px" }}
          size={{
            xs: 6
          }}>
          <Grid container spacing={0} direction="row" style={{ width: "180px" }}>
            { current_group }
          </Grid>
        </Grid>
      );
    } else if (infusions_by_level.length < 9) {
      for (let i = 0; i < infusions_by_level.length; ++i) {
        if (i === 5) {
          return_us.push(
            <Grid
              key={i}
              style={{ width: "180px" }}
              size={{
                xs: 6
              }}>
              <Grid container spacing={0} direction="row" style={{ width: "180px" }}>
                { current_group }
              </Grid>
            </Grid>
          );
          current_group = [];
        }
        const infusions_of_level = infusions_by_level[i];
        current_group.push(renderInfusionsOfLevel(infusions_of_level));
      }
      return_us.push(
        <Grid
          key="last"
          style={{ width: "180px" }}
          size={{
            xs: 6
          }}>
          <Grid container spacing={0} direction="row" style={{ width: "180px" }}>
            { current_group }
          </Grid>
        </Grid>
      );
    } else {
      for (let i = 0; i < infusions_by_level.length; ++i) {
        if (i === 4 || i === 7) {
          return_us.push(
            <Grid
              key={i}
              style={{ width: "180px" }}
              size={{
                xs: 4
              }}>
              <Grid container spacing={0} direction="row" style={{ width: "180px" }}>
                { current_group }
              </Grid>
            </Grid>
          );
          current_group = [];
        }
        const infusions_of_level = infusions_by_level[i];
        current_group.push(renderInfusionsOfLevel(infusions_of_level));
      }
      return_us.push(
        <Grid
          key="last"
          style={{ width: "180px" }}
          size={{
            xs: 4
          }}>
          <Grid container spacing={0} direction="row" style={{ width: "180px" }}>
            { current_group }
          </Grid>
        </Grid>
      );
    }
    return return_us;
  }

  const renderInfusionsOfLevel = (infusions_of_level: CharacterInfusionsOfLevel) => {
    const x_of_y = infusions_of_level.infused === infusions_of_level.total ? `${infusions_of_level.total}` : `${infusions_of_level.infused}/${infusions_of_level.total}`;
    const used = infusions_of_level.used;
    return (
      <Grid
        key={infusions_of_level.true_id}
        size={{
          xs: 12
        }}>
        { infusions_of_level.level }: { x_of_y }Infused, { used }Used
              </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const enabled = api.user !== null && (char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id));
    const return_me: any[] = [];
    // console.log(character.concentrating_on);
    if (char.concentrating_on) {
      return_me.push(
        <ConcentrationInput key="concentration" 
          character={char} 
          char_interface={char_interface} 
          disabled={!enabled} 
          shared={shared} 
          onChange={() => {
            char.changed();
            char.subscription_holder.recalculate();
            setReloading(true);
          }} 
        />
      );
    }
    const slot_types = Array.from(new Set(char_interface.use_slots.map(o => o.slot_name)));
    for (const slot_name of slot_types) {
      return_me.push(renderSlotsForType(slot_name));
    }
    if (char_interface.infusions_by_level.length > 0) {
      return_me.push(...renderInfusionsBySource());
    }
    for (const resource of char_interface.resources.filter(o => !o.is_slot)) {
      return_me.push(renderResource(resource));
    }
    if (return_me.length > 0) {
      return (
        <div className="bg_black_transp">
          <div
            style={{
              display: "flex",
              minHeight: "76px",
              overflowX: "auto" 
            }}>
            { return_me }
            { renderExtras() }
          </div>
        </div>
      );
    }
    return <></>;
  }
}
